import { Simulation } from '@/hooks/useSimulation';
import { Beneficiary } from '@/types/beneficiary';
import {
  CurrencyCodeType,
  DashboardOperation,
  FundingMethodItem,
  PaymentRailType,
} from '@/types/currency';
import { GenericAction } from '@/utility/reducers';

export interface TransferFlowState {
  readonly fundingMethod: FundingMethodItem;
  readonly fromAmount: number | null;
  readonly toAmount: number | null;
  readonly selectedOperation: DashboardOperation | null;
  readonly recipientAccountId: string;
  readonly paymentReference: string;
  readonly transferPurpose: string;
  readonly selectedBeneficiaryId: string;
  readonly beneficiaries: readonly Beneficiary[];
  readonly mainBeneficiaryId: string;
  readonly senderType: 'myself' | 'customer' | null;
  readonly sameDayAch: boolean;
  readonly markupBps: number | null;
  readonly amountInputSide: 'from' | 'to' | null;
}

export enum TransferFlowActionType {
  transactionFlowChanged = 'transactionFlowChanged',
  recipientAccountChanged = 'recipientAccountChanged',
  fromAmountChanged = 'fromAmountChanged',
  toAmountChanged = 'toAmountChanged',
  fundingMethodChanged = 'fundingMethodChanged',
  paymentReferenceChanged = 'paymentRefereceChanged',
  transferPurposeChanged = 'transferPurposeChanged',
  simulationCreated = 'simulationCreated',
  beneficiaryChanged = 'beneficiaryChanged',
  beneficiariesLoaded = 'beneficiariesLoaded',
  mainBeneficiarySet = 'mainBeneficiarySet',
  senderTypeChanged = 'senderTypeChanged',
  sameDayAchChanged = 'sameDayAchChanged',
  markupBpsChanged = 'markupBpsChanged',
}

export type TransactionFlowChangedAction = GenericAction<
  TransferFlowActionType.transactionFlowChanged,
  DashboardOperation
>;

export type RecipientAccountChangedAction = GenericAction<
  TransferFlowActionType.recipientAccountChanged,
  string
>;

export type FromAmountChangedAction = GenericAction<
  TransferFlowActionType.fromAmountChanged,
  number | null
>;

export type ToAmountChangedAction = GenericAction<
  TransferFlowActionType.toAmountChanged,
  number | null
>;

export type FundingMethodChangedAction = GenericAction<
  TransferFlowActionType.fundingMethodChanged,
  FundingMethodItem
>;

export type PaymentReferenceChangedAction = GenericAction<
  TransferFlowActionType.paymentReferenceChanged,
  string
>;

export type TransferPurposeChangedAction = GenericAction<
  TransferFlowActionType.transferPurposeChanged,
  string
>;

export type SimulationCreatedAction = GenericAction<
  TransferFlowActionType.simulationCreated,
  Simulation
>;

export type BeneficiaryChangedAction = GenericAction<
  TransferFlowActionType.beneficiaryChanged,
  string
>;

export type BeneficiariesLoadedAction = GenericAction<
  TransferFlowActionType.beneficiariesLoaded,
  readonly Beneficiary[]
>;

export type MainBeneficiarySetAction = GenericAction<
  TransferFlowActionType.mainBeneficiarySet,
  string
>;

export type SenderTypeChangedAction = GenericAction<
  TransferFlowActionType.senderTypeChanged,
  'myself' | 'customer' | null
>;

export type SameDayAchChangedAction = GenericAction<
  TransferFlowActionType.sameDayAchChanged,
  boolean
>;

export type MarkupBpsChangedAction = GenericAction<
  TransferFlowActionType.markupBpsChanged,
  number | null
>;

export type TransferFlowAction =
  | TransactionFlowChangedAction
  | RecipientAccountChangedAction
  | FromAmountChangedAction
  | ToAmountChangedAction
  | FundingMethodChangedAction
  | PaymentReferenceChangedAction
  | TransferPurposeChangedAction
  | SimulationCreatedAction
  | BeneficiaryChangedAction
  | BeneficiariesLoadedAction
  | MainBeneficiarySetAction
  | SenderTypeChangedAction
  | SameDayAchChangedAction
  | MarkupBpsChangedAction;

export const initialState: TransferFlowState = {
  fundingMethod: {
    id: '',
    method: PaymentRailType.unset,
    currency: CurrencyCodeType.UNSET,
  },
  fromAmount: null,
  toAmount: null,
  selectedOperation: null,
  recipientAccountId: '',
  paymentReference: '',
  transferPurpose: '',
  selectedBeneficiaryId: '',
  beneficiaries: [],
  mainBeneficiaryId: '',
  senderType: null,
  sameDayAch: false,
  markupBps: null,
  amountInputSide: null,
};

export const applySimulation = (
  state: TransferFlowState,
  simulation: Simulation,
): TransferFlowState => {
  const { from, to } = simulation;

  return {
    ...state,
    fromAmount: state.fromAmount ?? Number(from.value),
    toAmount: state.toAmount ?? Number(to.value),
  };
};

export const transferFlowReducer = (
  state: TransferFlowState,
  action: TransferFlowAction,
): TransferFlowState => {
  switch (action.type) {
    case TransferFlowActionType.transactionFlowChanged:
      return { ...state, selectedOperation: action.payload };
    case TransferFlowActionType.recipientAccountChanged:
      return {
        ...initialState,
        recipientAccountId: action.payload,
        fundingMethod: {
          id: '',
          method: PaymentRailType.unset,
          currency: CurrencyCodeType.UNSET,
        },
        selectedBeneficiaryId: state.selectedBeneficiaryId,
        beneficiaries: state.beneficiaries,
        mainBeneficiaryId: state.mainBeneficiaryId,
        senderType: state.senderType,
        sameDayAch: false,
      };
    case TransferFlowActionType.fromAmountChanged:
      return { ...state, fromAmount: action.payload, toAmount: null, amountInputSide: 'from' };
    case TransferFlowActionType.toAmountChanged:
      return { ...state, toAmount: action.payload, fromAmount: null, amountInputSide: 'to' };
    case TransferFlowActionType.fundingMethodChanged:
      return { ...state, fundingMethod: action.payload };
    case TransferFlowActionType.transferPurposeChanged:
      return { ...state, transferPurpose: action.payload };
    case TransferFlowActionType.paymentReferenceChanged:
      return { ...state, paymentReference: action.payload };
    case TransferFlowActionType.simulationCreated:
      return applySimulation(state, action.payload);
    case TransferFlowActionType.beneficiaryChanged:
      return {
        ...state,
        selectedBeneficiaryId: action.payload,
        fundingMethod: {
          id: '',
          method: PaymentRailType.unset,
          currency: CurrencyCodeType.UNSET,
        },
        fromAmount: null,
        toAmount: null,
      };
    case TransferFlowActionType.beneficiariesLoaded:
      return { ...state, beneficiaries: action.payload };
    case TransferFlowActionType.mainBeneficiarySet:
      return { ...state, mainBeneficiaryId: action.payload };
    case TransferFlowActionType.senderTypeChanged:
      return { ...state, senderType: action.payload };
    case TransferFlowActionType.sameDayAchChanged:
      return { ...state, sameDayAch: action.payload };
    case TransferFlowActionType.markupBpsChanged:
      return {
        ...state,
        markupBps: action.payload,
        fromAmount: state.amountInputSide === 'to' ? null : state.fromAmount,
        toAmount: state.amountInputSide === 'to' ? state.toAmount : null,
      };
    default:
      return state;
  }
};

export const transactionFlowChanged = (
  payload: DashboardOperation,
): TransactionFlowChangedAction => ({
  type: TransferFlowActionType.transactionFlowChanged,
  payload: payload,
});

export const recipientAccountChanged = (payload: string): RecipientAccountChangedAction => ({
  type: TransferFlowActionType.recipientAccountChanged,
  payload: payload,
});

export const fromAmountChanged = (payload: number | null): FromAmountChangedAction => ({
  type: TransferFlowActionType.fromAmountChanged,
  payload: payload,
});

export const toAmountChanged = (payload: number | null): ToAmountChangedAction => ({
  type: TransferFlowActionType.toAmountChanged,
  payload: payload,
});

export const fundingMethodChanged = (payload: FundingMethodItem): FundingMethodChangedAction => ({
  type: TransferFlowActionType.fundingMethodChanged,
  payload: payload,
});

export const paymentReferenceChanged = (payload: string): PaymentReferenceChangedAction => ({
  type: TransferFlowActionType.paymentReferenceChanged,
  payload: payload,
});

export const transferPurposeChanged = (payload: string): TransferPurposeChangedAction => ({
  type: TransferFlowActionType.transferPurposeChanged,
  payload: payload,
});

export const simulationCreated = (payload: Simulation): SimulationCreatedAction => ({
  type: TransferFlowActionType.simulationCreated,
  payload: payload,
});

export const beneficiaryChanged = (payload: string): BeneficiaryChangedAction => ({
  type: TransferFlowActionType.beneficiaryChanged,
  payload: payload,
});

export const beneficiariesLoaded = (
  payload: readonly Beneficiary[],
): BeneficiariesLoadedAction => ({
  type: TransferFlowActionType.beneficiariesLoaded,
  payload: payload,
});

export const mainBeneficiarySet = (payload: string): MainBeneficiarySetAction => ({
  type: TransferFlowActionType.mainBeneficiarySet,
  payload: payload,
});

export const senderTypeChanged = (
  payload: 'myself' | 'customer' | null,
): SenderTypeChangedAction => ({
  type: TransferFlowActionType.senderTypeChanged,
  payload: payload,
});

export const sameDayAchChanged = (payload: boolean): SameDayAchChangedAction => ({
  type: TransferFlowActionType.sameDayAchChanged,
  payload: payload,
});

export const markupBpsChanged = (payload: number | null): MarkupBpsChangedAction => ({
  type: TransferFlowActionType.markupBpsChanged,
  payload: payload,
});
